import React, { useEffect, useState } from "react";
import { bitcoin } from "../../assets/images";
import { RiArrowDownSFill, RiArrowUpSFill } from "react-icons/ri";
import { useCryptoData } from "../../hooks/useCryptoData";
import { Line } from "react-chartjs-2";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  ChartData,
} from "chart.js";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler
);

interface CoinDetails {
  price: string;
  change24Hour: string;
  changePc24Hour: string;
}

const CoinGraph = ({ selectedCoinObj }: any) => {
  const [coinDetails, setCoinDetails] = useState<CoinDetails>({
    price: "",
    change24Hour: "",
    changePc24Hour: "",
  });

  const [selectedRange, setSelectedRange] = useState<"1D" | "1W" | "1M" | "1Y">(
    "1W"
  );

  const crypto = selectedCoinObj?.abbr;
  const { data, isLoading } = useCryptoData(crypto, selectedRange);
  const cryptoData = data?.marketData || [];
  const coinData = data?.coinData;

  const usdData =
    crypto === "NGN"
      ? coinData?.DISPLAY?.USD?.NGN
      : coinData?.DISPLAY?.[crypto?.toUpperCase()]?.USD;

  useEffect(() => {
    setCoinDetails({
      price: usdData?.PRICE || "",
      change24Hour: usdData?.CHANGE24HOUR || "",
      changePc24Hour: usdData?.CHANGEPCT24HOUR || "",
    });
  }, [usdData]);

  const seriesData = cryptoData.map((item: any) => Number(item.price) || 0);

  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: {
        enabled: true,
        mode: "index",
        intersect: false,
        callbacks: {
          label: (tooltipItem: any) => `$${tooltipItem.formattedValue}`,
        },
      },
    },
    scales: {
      x: {
        display: false,
      },
      y: {
        display: false,
        ticks: {
          callback: (value: number) => `$${value.toLocaleString("en-US")}`,
        },
      },
    },
    elements: {
      line: { tension: 0.4, borderWidth: 3, borderColor: "#095B97" },
      point: { radius: 0 },
    },
  };

  const chartData = (canvas: HTMLCanvasElement): ChartData<"line"> => {
    const ctx = canvas.getContext("2d");
    if (!ctx) return { labels: [], datasets: [] };

    return {
      labels: seriesData.map((_: number, index: number) => index),
      datasets: [
        {
          label: `${selectedCoinObj?.abbr} Price`,
          data: seriesData,
          borderColor: "#095B97",
          tension: 0.4,
          pointRadius: 0,
          borderWidth: 2,
        },
      ],
    };
  };

  return (
    <div className="w-full font-manrope bg-white dark:bg-[#1C2227] rounded-xl pt-5 pb-8 px-6">
      <h3 className="font-bold text-paragraph text-[16px] dark:text-white">
        {selectedCoinObj?.name} price
      </h3>

      <div className="mt-10 flex flex-col justify-center items-center gap-1">
        <div className="flex justify-center items-center  w-[44px] h-[44px] rounded-full">
          <img
            src={selectedCoinObj?.logo}
            className="w-full h-full bg-cover"
            alt=""
          />
        </div>
        <h4 className="text-[13px] text-[#4E606E] dark:text-gray-300 mt-2">
          Current {selectedCoinObj?.abbr} Price
        </h4>
        <h4 className="text-[24px] font-sora font-bold text-[#13181B] dark:text-gray-200 ">
          {isLoading ? "----" : coinDetails.price}
        </h4>
        <div className="flex text-[11px] gap-1 mt-4 items-center">
          {Number(coinDetails.changePc24Hour) > 0 ? (
            <RiArrowUpSFill className="text-[14px] text-[#0D851D]" />
          ) : (
            <RiArrowDownSFill className="text-[14px] text-red-500" />
          )}

          <h4
            className={
              Number(coinDetails.changePc24Hour) > 0
                ? "text-[#0D851D] font-medium"
                : "text-red-500 font-medium"
            }
          >
            {coinDetails.changePc24Hour}% ({coinDetails.change24Hour})
            <span className="text-gray-500 dark:text-gray-400"> (24h)</span>
          </h4>
        </div>
        <div className="w-full mt-6 py-4">
          <Line
            data={chartData(document.createElement("canvas"))}
            options={chartOptions as any}
          />
        </div>
        <div className="flex justify-center gap-5 mb-4 mt-6">
          {["1D", "1W", "1M", "1Y", "ALL"].map((range) => (
            <button
              key={range}
              onClick={() => setSelectedRange(range as any)}
              className={`text-sm px-3 py-1   ${
                selectedRange === range
                  ? " text-[#0B75C2] border-[#0B75C2] border-b-2  font-bold "
                  : "text-gray-900 dark:text-gray-100 "
              }`}
            >
              {range}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};

export default CoinGraph;
